import { useMemo, useState } from 'react'
import {
  Box, Dialog, FormControl, IconButton, MenuItem,
  Paper, Select, Tooltip, Typography,
} from '@mui/material'
import TableChartIcon  from '@mui/icons-material/TableChart'
import FullscreenIcon  from '@mui/icons-material/Fullscreen'
import DownloadIcon    from '@mui/icons-material/Download'
import CloseIcon       from '@mui/icons-material/Close'
import GradientIcon    from '@mui/icons-material/Gradient'
import { DataGrid } from '@mui/x-data-grid'
import { useDispatch, useSelector } from 'react-redux'
import LoadingOverlay from './Beacon4LoadingOverlay'
import {
  selectBeacon4PerformanceTable,
  selectBeacon4PerformanceTableLoading,
  selectBeacon4PerformanceAvailableKpisByTech,
} from '../reducers/Beacon4PerformanceKpiDataReducer'
import {
  selectBeacon4PerfAggregationMode,
  selectBeacon4PerfTech,
  selectBeacon4PerfMainKpi,
  selectBeacon4PerfTableKpi,
} from '../reducers/Beacon4FiltersReducer'
import { reloadBeacon4PerformanceTableOnly } from '../actions/Beacon4PerformanceModuleActions'

// Must match the Aggregation Mode values the backend accepts. Kept here
// (not imported from FilterBar) so this component owns its own controls.
const AGG_MODES = ['Site', 'Mycom ID', 'Side', 'Polygon', 'Vendor']

// Field names + pixel widths of the leading identity column(s). GROUP_COL is
// always present; SECONDARY (Site) and TERTIARY (Vendor) are optional extras
// only shown for AggregationMode === "Mycom ID" (see
// Beacon4PerformanceTableRepository.Build), giving a 1-, 2- or 3-column
// leading block: [Mycom ID] or [Site, Mycom ID] or [Site, Mycom ID, Vendor].
// Kept as constants so the column defs, the sticky-offset calculation and
// initialState.pinnedColumns all agree on the same field names/widths.
const GROUP_COL_FIELD     = 'groupKey'
const GROUP_COL_WIDTH     = 150
const SECONDARY_COL_FIELD = 'secondaryGroupKey'
const SECONDARY_COL_WIDTH = 130
const TERTIARY_COL_FIELD  = 'tertiaryGroupKey'
const TERTIARY_COL_WIDTH  = 110

// ── Heatmap coloring ─────────────────────────────────────────────────────────
// Colors cells on a light→dark gradient by magnitude. The hue family (green
// vs red) is picked from the KPI's Direction (kpi_config.is_rate):
//   isRate = false → "Higher is better"  → green family
//   isRate = true  → "Lower is better"   → red family
// This is a pure magnitude gradient (light = low value, dark = high value);
// direction only chooses the color family, it does not judge good vs bad.
const HEATMAP_STOPS = {
  higherIsBetter: { light: [232, 245, 233], dark: [27, 94, 32] },   // #e8f5e9 → #1b5e20 (green)
  lowerIsBetter:  { light: [255, 235, 238], dark: [183, 28, 28] },  // #ffebee → #b71c1c (red)
}

// Small in-house linear RGB interpolation — avoids pulling in a color-scale
// library just for this one gradient.
function lerpColor(from, to, t) {
  const r = Math.round(from[0] + (to[0] - from[0]) * t)
  const g = Math.round(from[1] + (to[1] - from[1]) * t)
  const b = Math.round(from[2] + (to[2] - from[2]) * t)
  return `rgb(${r}, ${g}, ${b})`
}

function heatmapStyleFor(value, min, max, isRate) {
  if (value == null || !isFinite(value) || min == null || max == null) return null
  const t = max === min ? 0.5 : Math.min(1, Math.max(0, (value - min) / (max - min)))
  const stops = isRate ? HEATMAP_STOPS.lowerIsBetter : HEATMAP_STOPS.higherIsBetter
  return {
    bg: lerpColor(stops.light, stops.dark, t),
    // Once the background is dark enough, switch to white text for contrast.
    color: t > 0.55 ? '#fff' : 'inherit',
  }
}

const gridSx = {
  border: 'none',
  fontSize: 11,
  '& .MuiDataGrid-columnHeaders': { bgcolor: '#fafafa', borderBottom: '1px solid', borderColor: 'divider' },
  '& .MuiDataGrid-columnHeaderTitle': { fontSize: 10, fontWeight: 700, color: 'text.secondary' },
  // Solid (not translucent) — this cell now also has to fully hide whatever
  // date column scrolls underneath it once it's sticky (see .b4p-frozen-col
  // below), so it can no longer be a semi-transparent tint over the row.
  '& .b4p-row-header': { fontWeight: 600, fontFamily: 'monospace', fontSize: 11, bgcolor: '#fbfbfb' },
  '& .MuiDataGrid-cell': { borderColor: 'divider', py: 0.5 },
  // Heatmap cells fill the whole cell with their own background via
  // renderCell, so kill the default cell padding that would leave a plain
  // white margin around the color.
  '& .MuiDataGrid-cell.b4p-heat-cell': { padding: 0 },
  // Excel-style frozen leading column(s) (Mycom ID, plus optionally Site and/
  // or Vendor ahead of it). `initialState.pinnedColumns` below is the "real"
  // MUI mechanism for this, but it's a DataGridPro-only feature — this
  // project uses the free `@mui/x-data-grid` package, where that prop is
  // silently ignored, so the column never actually froze despite being
  // configured. This CSS-only `position: sticky` is the actual fix: it works
  // in the Community package because DataGrid renders each row as a plain
  // flex container with no `overflow` of its own, so `left` sticks relative
  // to the grid's horizontally-scrolling viewport. Needs a solid background
  // (no transparency) and a z-index above the scrolling date columns, or
  // their content would show/bleed through as they scroll past.
  //
  // `left` itself is NOT set here — there can be 1, 2 or 3 of these columns
  // depending on aggregation mode, each with a different cumulative offset,
  // so it's computed per-field (via data-field attribute selectors) in
  // `frozenOffsetSx`, built alongside the columns below and merged into this
  // sx object where the DataGrid is rendered.
  '& .b4p-frozen-col': {
    position: 'sticky',
    zIndex: 2,
    borderRight: '1px solid',
    borderColor: 'divider',
  },
  '& .MuiDataGrid-columnHeader.b4p-frozen-col': { bgcolor: '#fafafa', zIndex: 3 },
}

/**
 * Renders the aggregation-mode breakdown table:
 *   - Rows    = each group value (Site / Mycom / Polygon / Side / Vendor)
 *   - Columns = dates (respecting the Daily/Weekly/Monthly time aggregation)
 *   - Cells   = KPI value (chosen in the header dropdown) for that group on that date
 *
 * The table has its OWN KPI picker — it defaults to the Main KPI on Apply,
 * then the user can switch to any sub-KPI of the same tech without re-running
 * Apply (only the table is refetched). The header (including the picker) is
 * always rendered so users can change the KPI even while the table is loading
 * or empty.
 *
 * The group-key column (Site / Polygon / ... value) is frozen — header and
 * cells stay visible while scrolling horizontally through the date columns —
 * via plain CSS `position: sticky` (the `.b4p-frozen-col` class in gridSx),
 * not MUI's `pinnedColumns`, which requires the Pro package this project
 * doesn't have.
 *
 * Cells are optionally heatmap-colored by value (see HEATMAP_STOPS above),
 * toggle in the header bar. Requires the backend to return `isRate` on the
 * table response (kpi_config's Direction) — see comment on `table?.isRate`.
 *
 * A Maximize button (matching the charts' Fullscreen affordance) opens the
 * same table full-screen, useful when there are many group rows or date
 * columns and the inline panel feels cramped.
 */
export default function Beacon4PerformanceAggregationTable() {
  const dispatch  = useDispatch()
  const table     = useSelector(selectBeacon4PerformanceTable)
  const loading   = useSelector(selectBeacon4PerformanceTableLoading)
  const aggMode   = useSelector(selectBeacon4PerfAggregationMode)
  const tech      = useSelector(selectBeacon4PerfTech)
  const mainKpi   = useSelector(selectBeacon4PerfMainKpi)
  const tableKpi  = useSelector(selectBeacon4PerfTableKpi)
  const kpisByTech = useSelector(selectBeacon4PerformanceAvailableKpisByTech)

  const [maximized, setMaximized] = useState(false)
  const [heatmap, setHeatmap]     = useState(false)

  // All active KPIs of the current tech, exactly as the backend returns them
  // (Main + every Breakdown/Breakdown2 — independent of which Main KPI drives
  // the dashboard). The Main is annotated in the option list below.
  const kpiOptions = useMemo(() => kpisByTech?.[tech] ?? [], [kpisByTech, tech])

  // What the dropdown shows: fall back to mainKpi until the user has applied
  // (which seeds tableKpi). If neither is set, leave it empty.
  const selectedKpi = tableKpi || mainKpi || ''

  const onChangeKpi = (key) => {
    if (!key || key === selectedKpi) return
    dispatch(reloadBeacon4PerformanceTableOnly({ kpiKey: key }))
  }

  const onChangeAggMode = (mode) => {
    if (!mode || mode === aggMode) return
    dispatch(reloadBeacon4PerformanceTableOnly({ aggMode: mode }))
  }

  // Color-scale bounds for the heatmap: the 5th and 95th percentile of every
  // value in the table (all rows × all dates), NOT the absolute min/max.
  // A single extreme outlier (e.g. a KPI formula glitch producing a wildly
  // large or very negative value in one cell) would otherwise dominate the
  // absolute min/max and compress every "normal" cell toward one end of the
  // gradient, making them all look the same shade regardless of their real
  // differences. Values beyond these bounds are simply clamped to the
  // darkest/lightest stop (heatmapStyleFor already clamps t to [0,1]) —
  // they show as fully saturated rather than stretching the scale to fit them.
  const valueRange = useMemo(() => {
    if (!table?.rows?.length) return { min: null, max: null }

    const values = []
    table.rows.forEach(r => {
      table.dates.forEach(d => {
        const v = r.values?.[d]
        if (typeof v === 'number' && isFinite(v)) values.push(v)
      })
    })
    if (!values.length) return { min: null, max: null }

    values.sort((a, b) => a - b)
    const percentile = (p) => {
      const idx = Math.min(values.length - 1, Math.max(0, Math.floor(p * (values.length - 1))))
      return values[idx]
    }
    let min = percentile(0.05)
    let max = percentile(0.95)

    // If the percentile bounds collapse onto the SAME value, fall back to the
    // true min/max instead. This happens for ceiling-heavy metrics like
    // Availability, where the vast majority of cells sit exactly at 100 —
    // both the 5th and 95th percentile land on 100 too, which would otherwise
    // give every cell the exact same flat color and hide the very outliers
    // (e.g. an outage dipping to 79%) a heatmap exists to surface.
    if (max === min) {
      min = values[0]
      max = values[values.length - 1]
    }

    if (!isFinite(min) || !isFinite(max) || max === min) return { min: null, max: null }
    return { min, max }
  }, [table])

  const { columns, rows, hasSecondary, hasTertiary, secondaryLabel, leadingFields, frozenOffsetSx } = useMemo(() => {
    if (!table) return { columns: [], rows: [], hasSecondary: false, hasTertiary: false, secondaryLabel: 'Site', leadingFields: [GROUP_COL_FIELD], frozenOffsetSx: {} }

    // isRate comes from kpi_config's Direction for the selected KPI. Falls
    // back to "Higher is better" (green) if the backend hasn't been updated
    // yet to include it on the table response.
    const isRate = Boolean(table.isRate)

    // The backend only populates secondary/tertiaryGroupKey for Mycom ID and
    // Site modes — every other mode leaves them null on every row. Checking
    // the actual data (not just the aggMode check) means an older, not-yet-
    // redeployed backend that doesn't send these fields yet just quietly
    // falls back to fewer leading columns instead of showing an empty one.
    //   Mycom ID → secondary = Site, tertiary = Vendor  → [Site, Mycom ID, Vendor]
    //   Site     → secondary = Vendor, no tertiary      → [Site, Vendor]
    const hasSecondary = (aggMode === 'Mycom ID' || aggMode === 'Site') && table.rows.some(r => r.secondaryGroupKey != null)
    const hasTertiary  = aggMode === 'Mycom ID' && table.rows.some(r => r.tertiaryGroupKey != null)
    const secondaryLabel = aggMode === 'Site' ? 'Vendor' : 'Site'

    // 1, 2 or 3 leading identity columns. Built as a list (not separate
    // hardcoded column defs) so the sticky `left` offset of each one can be
    // computed as a simple running total below, however many are present.
    //
    // Order depends on mode, since "secondary" means something different
    // each time: for Mycom ID, Site is the meaningful context the user reads
    // first, so it leads (Site, Mycom ID, Vendor). For Site mode, the
    // primary GroupKey IS Site — it has to lead — with Vendor trailing as
    // extra description (Site, Vendor), not the other way around.
    const secondaryCol = { field: SECONDARY_COL_FIELD, headerName: secondaryLabel, width: SECONDARY_COL_WIDTH }
    const primaryCol   = { field: GROUP_COL_FIELD, headerName: aggMode || 'Group', width: GROUP_COL_WIDTH }
    const leadingDefs = aggMode === 'Mycom ID'
      ? [
          ...(hasSecondary ? [secondaryCol] : []),
          primaryCol,
          ...(hasTertiary ? [{ field: TERTIARY_COL_FIELD, headerName: 'Vendor', width: TERTIARY_COL_WIDTH }] : []),
        ]
      : [
          primaryCol,
          ...(hasSecondary ? [secondaryCol] : []),
        ]

    let runningLeft = 0
    const leadingWithOffset = leadingDefs.map(d => {
      const left = runningLeft
      runningLeft += d.width
      return { ...d, left }
    })

    // Per-field sticky offset, targeted via the `data-field` attribute MUI
    // DataGrid puts on every cell/header — merged into gridSx where the grid
    // is rendered. This is what actually varies (1/2/3 columns, each a
    // different width combination); `.b4p-frozen-col` in gridSx only carries
    // the parts that never change (position, z-index, border, background).
    const frozenOffsetSx = leadingWithOffset.reduce((acc, d) => {
      acc[`& .MuiDataGrid-cell[data-field="${d.field}"]`]          = { left: d.left }
      acc[`& .MuiDataGrid-columnHeader[data-field="${d.field}"]`]  = { left: d.left }
      return acc
    }, {})

    const cols = [
      ...leadingWithOffset.map(d => ({
        field: d.field,
        headerName: d.headerName,
        width: d.width,
        sortable: true,
        cellClassName: 'b4p-row-header b4p-frozen-col',
        // `headerClassName` — not `cellClassName` — is how MUI lets a column
        // def style its own header cell; `b4p-frozen-col` (plus the per-field
        // `left` from frozenOffsetSx) is what actually keeps these columns
        // visible while scrolling horizontally (see gridSx — it's a plain
        // CSS `position: sticky`, not MUI's Pro-only `pinnedColumns`, which
        // the free package ignores).
        headerClassName: 'b4p-frozen-col',
      })),
      ...table.dates.map(d => ({
        field: d,
        headerName: d,
        width: 105,
        type: 'number',
        sortable: true,
        // Explicit numeric comparator — don't rely on the grid inferring
        // numeric sort purely from `type: 'number'`. This guarantees a real
        // numeric comparison (9.5 sorts before 10.2) instead of a possible
        // text/lexicographic fallback (which would put "10.2" before "9.5"
        // because '1' < '9' as characters). Nulls sort to one consistent end
        // instead of being compared as NaN.
        sortComparator: (v1, v2) => {
          const a = v1 == null ? -Infinity : Number(v1)
          const b = v2 == null ? -Infinity : Number(v2)
          return a - b
        },
        align: 'right',
        headerAlign: 'right',
        valueGetter: (params) => params.row[d],
        cellClassName: heatmap ? 'b4p-heat-cell' : undefined,
        renderCell: (params) => {
          const v = params.value
          const text = v == null ? '—' : Number(v).toFixed(2) + (table.unit === '%' ? '' : '')
          const style = heatmap ? heatmapStyleFor(v, valueRange.min, valueRange.max, isRate) : null
          return (
            <Box sx={{
              width: '100%', height: '100%',
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
              px: 1.25,
              bgcolor: style?.bg ?? 'transparent',
              color: v == null ? 'text.disabled' : (style?.color ?? 'inherit'),
            }}>
              {text}
            </Box>
          )
        },
      })),
    ]

    const rs = table.rows.map((r, i) => {
      // groupKey alone is no longer guaranteed unique: the same Mycom ID can
      // legitimately appear on more than one row now (once per distinct
      // Site/Vendor it reports under — see Beacon4PerformanceKpiDataService),
      // which would otherwise give the DataGrid two rows sharing one `id`
      // and make it silently drop/merge one of them. Combine every leading
      // key that's actually present so each (Site, Mycom ID, Vendor)
      // combination gets its own unique row id; every other aggregation
      // mode has no secondary/tertiaryGroupKey and keeps using groupKey
      // alone, unchanged.
      const idParts = [r.secondaryGroupKey, r.groupKey, r.tertiaryGroupKey].filter(v => v != null)
      return {
        id: idParts.length ? idParts.join('__') : `row-${i}`,
        groupKey: r.groupKey,
        secondaryGroupKey: r.secondaryGroupKey ?? null,
        tertiaryGroupKey: r.tertiaryGroupKey ?? null,
        ...r.values,
      }
    })

    return {
      columns: cols,
      rows: rs,
      hasSecondary,
      hasTertiary,
      secondaryLabel,
      leadingFields: leadingWithOffset.map(d => d.field),
      frozenOffsetSx,
    }
  }, [table, aggMode, heatmap, valueRange])

  // Exports the FULL table (every group row × every date column), not just
  // the rows currently visible on the DataGrid's page — the grid's own
  // pagination only limits what's rendered on screen, the underlying
  // `table` data already holds everything the backend returned.
  const exportCsv = () => {
    if (!table || !table.rows?.length) return

    const escape = (v) => {
      const s = String(v ?? '')
      return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }

    const header = [
      ...(hasSecondary ? [secondaryLabel] : []),
      aggMode || 'Group',
      ...(hasTertiary ? ['Vendor'] : []),
      ...table.dates,
    ]
    const lines = [header.map(escape).join(',')]

    table.rows.forEach(r => {
      const cells = [
        ...(hasSecondary ? [r.secondaryGroupKey ?? ''] : []),
        r.groupKey,
        ...(hasTertiary ? [r.tertiaryGroupKey ?? ''] : []),
        ...table.dates.map(d => {
          const v = r.values?.[d]
          return v == null ? '' : Number(v).toFixed(2)
        }),
      ]
      lines.push(cells.map(escape).join(','))
    })

    // Leading BOM so Excel opens the file as UTF-8 instead of guessing wrong.
    const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' })
    const url  = URL.createObjectURL(blob)
    const safeName = `${table.kpiName || 'kpi'}_${aggMode || 'group'}`.replace(/[^a-z0-9]+/gi, '_')

    const a = document.createElement('a')
    a.href = url
    a.download = `${safeName}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // Shared header bar — used both inline and inside the maximized dialog.
  // `onMaximize` / `onClose` are mutually exclusive: pass whichever applies.
  const renderHeaderBar = ({ onMaximize, onClose }) => (
    <Box sx={{
      px: 1.5, py: 0.75,
      bgcolor: 'grey.100',
      borderBottom: '1px solid', borderColor: 'divider',
      display: 'flex', alignItems: 'center', gap: 1,
      flexShrink: 0,
    }}>
      <TableChartIcon sx={{ fontSize: 14, color: 'text.secondary' }} />

      {/* KPI picker — defaults to Main KPI after Apply; switching it refetches only the table */}
      <FormControl size="small">
        <Select
          value={kpiOptions.some(k => k.key === selectedKpi) ? selectedKpi : ''}
          onChange={e => onChangeKpi(e.target.value)}
          displayEmpty
          disabled={!kpiOptions.length}
          sx={{
            height: 24, fontSize: 11, minWidth: 220, bgcolor: 'background.paper',
            '& .MuiSelect-select': { py: 0.25 },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
          }}
        >
          <MenuItem value="" disabled sx={{ fontSize: 11, color: 'text.disabled' }}>
            (Select KPI)
          </MenuItem>
          {kpiOptions.map(k => (
            <MenuItem key={k.key} value={k.key} sx={{ fontSize: 11 }}>
              {k.label}{k.key === mainKpi ? '  · main' : ''}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>by</Typography>

      {/* Aggregation Mode picker — moved here from the FilterBar so all
          table-specific controls live in one place; instant refetch on change. */}
      <FormControl size="small">
        <Select
          value={aggMode || ''}
          onChange={e => onChangeAggMode(e.target.value)}
          sx={{
            height: 24, fontSize: 11, minWidth: 120, bgcolor: 'background.paper',
            '& .MuiSelect-select': { py: 0.25 },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
          }}
        >
          {AGG_MODES.map(m => (
            <MenuItem key={m} value={m} sx={{ fontSize: 11 }}>{m}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {table?.unit && (
        <Typography sx={{ fontSize: 10, color: 'text.disabled', ml: 0.5 }}>
          ({table.unit})
        </Typography>
      )}
      <Box sx={{ flex: 1 }} />
      {/* The header's own small "Loading…" badge used to be the only
          feedback for a table refresh — easy to miss. Removed in favor of
          the bigger dimming overlay now on the body (see renderBody), which
          also keeps this header bar (KPI/Aggregation Mode dropdowns) fully
          usable and uncluttered while a request is in flight. */}
      {!loading && table && (
        <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
          {rows.length} rows · {table.dates.length} dates
        </Typography>
      )}

      <Tooltip title={
        (heatmap ? 'Hide' : 'Show') +
        ' heatmap coloring. Light = low value, dark = high value (based on the 5th-95th percentile — extreme outliers show fully saturated). Green = higher is better, red = lower is better.'
      }>
        <IconButton size="small" onClick={() => setHeatmap(h => !h)}
          sx={{ p: 0.25, color: heatmap ? 'primary.main' : 'text.disabled', '&:hover': { color: 'primary.main' } }}>
          <GradientIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      <Tooltip title="Export all rows to CSV">
        <span>
          <IconButton size="small" onClick={exportCsv} disabled={!table || rows.length === 0}
            sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
            <DownloadIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>

      {onMaximize && (
        <Tooltip title="Maximize table">
          <IconButton size="small" onClick={onMaximize} sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
            <FullscreenIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
      {onClose && (
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  )

  // Body — swaps between empty / data. Loading is shown in the header bar
  // so the dropdown stays usable; the body keeps its previous content if any.
  const renderBody = () => (
    <Box sx={{ position: 'relative', minHeight: 0, width: '100%', display: 'flex', alignItems: 'flex-start' }}>
      {/* Own overlay, independent of the charts' — this table is a separate
          fetch (GetAggregationTable) from the dashboard series, so it can be
          reloading on its own (e.g. KPI/Aggregation Mode changed here) while
          the charts above sit idle, or vice versa. */}
      {loading && <LoadingOverlay radius={0} />}
      {!table || rows.length === 0 ? (
        <Box sx={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          color: 'text.disabled', p: 4, textAlign: 'center',
        }}>
          {!loading && (
            <>
              <TableChartIcon sx={{ fontSize: 36, opacity: 0.3 }} />
              <Typography sx={{ fontSize: 12, mt: 0.5 }}>
                No table data yet — pick a KPI and press Apply.
              </Typography>
            </>
          )}
        </Box>
      ) : (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            density="compact"
            disableRowSelectionOnClick
            // Sizes the grid to exactly fit the current page's rows (25/50/100,
            // whatever "Rows per page" is set to) instead of a fixed container
            // height with its own internal scrollbar cutting a page in half.
            // The page as a whole scrolls if this makes the table taller than
            // the viewport — see the overflowY:'auto' on the outer
            // Beacon4PerformanceView container.
            autoHeight
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              // NOT the freezing mechanism, despite what this looks like —
              // `pinnedColumns` only does anything on @mui/x-data-grid-pro.
              // This project uses the free @mui/x-data-grid, which silently
              // ignores it. Left here as a harmless no-op so that IF this
              // package is ever upgraded to the Pro tier, the "real" MUI
              // pinning mechanism starts working immediately with no other
              // changes needed. The actual freeze today is the CSS-only
              // `.b4p-frozen-col` (position: sticky) in gridSx below.
              pinnedColumns: { left: leadingFields },
            }}
            // NOT `loading={loading}` — MUI's DataGrid renders its OWN
            // internal loading overlay when that prop is true, which used to
            // stack on top of our LoadingOverlay above (the two loading UIs
            // rendering at once, misaligned, is exactly the "sobreposição"
            // bug this caused). Our overlay already covers this whole Box,
            // including the grid, so the grid itself doesn't need to know
            // it's loading at all.
            sx={{ ...gridSx, ...frozenOffsetSx }}
          />
        </Box>
      )}
    </Box>
  )

  return (
    <>
      <Paper sx={{
        overflow: 'hidden',
        height: '100%', display: 'flex', flexDirection: 'column',
      }}>
        {renderHeaderBar({ onMaximize: () => setMaximized(true) })}
        {renderBody()}
      </Paper>

      {/* Maximized view — same table, full screen. Re-rendered (not just
          CSS-scaled) so the DataGrid measures the larger viewport correctly. */}
      <Dialog
        open={maximized}
        onClose={() => setMaximized(false)}
        fullScreen
        PaperProps={{ sx: { display: 'flex', flexDirection: 'column' } }}
      >
        {renderHeaderBar({ onClose: () => setMaximized(false) })}
        {renderBody()}
      </Dialog>
    </>
  )
}